import React, { useState } from "react";
import { CircularProgress, Typography, Box } from "@mui/material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

import { useStyles } from "./UploadStatus.style";
import { readableSize } from "./../../../commons/utils";

export enum Status {
  Idle,
  Transmitting,
  WaitingForResponse,
  Success,
  Error,
}

interface UploadStatusProps {
  status: Status;
  countFiles: number;
  bytesCompleted: number;
  bytesTotal: number;
}

export const UploadStatus: React.FC<UploadStatusProps> = ({
  status,
  countFiles,
  bytesCompleted,
  bytesTotal,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [start] = useState(new Date());

  const calcPercent = () => {
    if (
      isNaN(bytesCompleted) ||
      bytesCompleted < 0 ||
      isNaN(bytesTotal) ||
      bytesTotal <= 0
    )
      return 0;
    return Math.round((bytesCompleted * 100) / bytesTotal);
  };

  const calcSecondsLeft = () => {
    let result = 0;
    if (status === Status.Transmitting) {
      const duration = new Date().valueOf() - start.valueOf();
      if (duration > 0 && bytesCompleted > 0) {
        result = Math.round(
          (bytesTotal - bytesCompleted) / ((bytesCompleted / duration) * 1000)
        );
      }
    }
    return result;
  };

  const readableTime = (seconds: number) => {
    if (isNaN(seconds)) return t("upload.status.seconds", { seconds: 0 });
    if (seconds >= 3600)
      return t("upload.status.hours", {
        hours: Math.floor(seconds / 3600),
      });
    if (seconds >= 60)
      return t("upload.status.minutes", {
        minutes: Math.floor((seconds % 3600) / 60),
      });
    return t("upload.status.seconds", { seconds: seconds });
  };

  const idle = () => {
    return (
      <>
        <HourglassEmptyIcon color="primary" fontSize="large" />
        <Box>
          <Typography color="primary">
            {t("upload.status.sending", { files: countFiles })}
          </Typography>
          <Typography color="primary">
            {t("upload.status.uploaded", {
              completed: readableSize(bytesCompleted),
              total: readableSize(bytesTotal),
            })}
          </Typography>
          <Typography color="primary">
            {t("upload.status.remaining", {
              left: readableTime(calcSecondsLeft()),
            })}
          </Typography>
        </Box>
      </>
    );
  };

  const transferring = () => {
    const percent = calcPercent();
    return (
      <>
        <Box position="relative" display="inline-flex">
          <CircularProgress
            variant="indeterminate"
            value={percent}
            size="5rem"
          />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="h6" color="primary">
              {percent}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              %
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="h5" color="primary">
            {t("upload.status.transferring")}
          </Typography>
          <Typography color="primary">
            {t("upload.status.sending", { files: countFiles })}
          </Typography>
          <Typography color="primary">
            {t("upload.status.uploaded", {
              completed: readableSize(bytesCompleted),
              total: readableSize(bytesTotal),
            })}
          </Typography>
          <Typography color="primary">
            {t("upload.status.remaining", {
              left: readableTime(calcSecondsLeft()),
            })}
          </Typography>
        </Box>
      </>
    );
  };

  const waiting = () => {
    return (
      <>
        <HourglassEmptyIcon color="primary" fontSize="large" />
        <Typography variant="h6" color="primary">
          {t("upload.status.waitingForResponse")}
        </Typography>
      </>
    );
  };

  const success = () => (
    <>
      <DoneIcon color="primary" fontSize="large" />
      <Typography variant="h5" color="primary">
        {t("upload.status.completed")}
      </Typography>
    </>
  );

  const error = () => (
    <>
      <CloseIcon style={{ color: "red" }} fontSize="large" />
      <Typography variant="h5" style={{ color: "red" }}>
        {t("upload.status.failed")}
      </Typography>
    </>
  );

  const content = () => {
    switch (status) {
      case Status.Idle:
        return idle();
      case Status.Transmitting:
        return transferring();
      case Status.WaitingForResponse:
        return waiting();
      case Status.Success:
        return success();
      case Status.Error:
        return error();
      default:
        return (
          <>
            <Typography>Unknown status</Typography>
          </>
        );
    }
  };

  return <div className={classes.root}>{content()}</div>;
};
